// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O QUE O PLANEADOR VÊ QUANDO OUTRA PESSOA JÁ LÁ MEXEU
// ============================================================================
//
// 📄 §11 linha O, peça **P3**. 📗 Migração 091 autorizada pelo cliente a 2026-09-28.
//
// 📗 **O desenho estava fixado, e é este:**
//
//    > *«A viatura 136, dias 283-285, foi alterada às 14:32 por Ana Costa — depois de abrires
//    > este ecrã.»*
//    > → **«Gravar mesmo assim»** + **motivo obrigatório** · ou **«Ver o que mudou»**, que leva
//    > ao diário em `outros`
//
// ---------------------------------------------------------------------------
// 🔴 D-007: AVISA, PEDE MOTIVO, DEIXA AVANÇAR — nunca um `disabled`
// ---------------------------------------------------------------------------
//
//    *«Um sistema que impede produz um caderno.»* O botão de gravar **existe sempre**; o que
//    ele exige é a razão escrita. O que protege a empresa não é o sistema ter impedido — é
//    haver prova de que avisou e de que uma pessoa decidiu com motivo (art. 10.º n.º 3).
//
// ⚠️ **O botão fica desactivado SÓ por falta de motivo**, que é a mesma regra do
//    `SaidaDoTravao` e tem a frase ao lado a dizer porquê — um botão apagado sem explicação é
//    indistinguível de um botão avariado.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ NOMEIA O QUE MUDOU E QUEM — «alguém mexeu» é um beco
// ---------------------------------------------------------------------------
//
//    A frase do servidor já vem nomeada (executor, intervalo de dias, hora, pessoa). Aqui
//    mostra-se **também a tabela**, linha a linha, porque a frase agrega por executor e quem
//    está a decidir quer ver os dias. 📌 E quando o servidor manda mais linhas do que a amostra
//    (tecto de 10 no `trigger`), **diz-se quantas faltam**: um total que não bate com a lista
//    lê-se como defeito.
//
// ---------------------------------------------------------------------------
// ✅ O «VER O QUE MUDOU» É O CAMINHO QUE JÁ EXISTE — não é um segundo
// ---------------------------------------------------------------------------
//
//    O `PlanoMudou.tsx` já abre o `Diario` em **`outros`** desde `595ce713`, e pela razão
//    exacta que serve aqui: quem carrega acabou de ler que a mudança **não foi sua**, e a lista
//    que responde a isso é a dos outros. Reaproveita-se o componente e o filtro; escrever um
//    segundo caminho para a mesma pergunta era duas verdades sobre o mesmo plano.
//
// ⚠️ **O diário abre POR CIMA e a caixa não fecha.** Fechá-la para mostrar o diário perdia o
//    motivo já escrito e obrigava a repetir o gesto para voltar aqui — e o gesto é uma escrita.
//
// ---------------------------------------------------------------------------
// ⚠️ NÃO É UMA JANELA ARRASTÁVEL, e é deliberado
// ---------------------------------------------------------------------------
//
//    As janelas deste sistema são arrastáveis porque 📗 *«quero ver o mapa enquanto selecciono a
//    opção»*. Aqui não há nada para ver no mapa: o mapa que está por baixo é **o retrato
//    velho**, que é precisamente o problema. Arrastá-la para ir confirmar no mapa levava a
//    pessoa a confirmar contra os dados desactualizados.
//
// ⚠️ **E não tem «X» de fechar sem decidir por engano:** sai-se por `Escape` ou por «Não gravar»,
//    e as duas dizem o mesmo — a escrita **não** aconteceu. 📌 Um `X` ao lado de «Gravar mesmo
//    assim» é a única coisa nesta caixa que se pode carregar por reflexo.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import {
  registarPerguntadorDoConflito,
  type CorpoDoConflito,
  type DecisaoDoConflito,
  type LinhaEmConflito,
} from './conflito.js';
import { Diario } from './Diario.js';
import { useEscape } from './janela.js';
import { MOTIVO_EXEMPLO, MOTIVO_SUFIXO } from './travao-no-ecra.js';

/** O executor da linha. ⚠️ Uma alocação tem exactamente um (ADR-003 §4-ter). */
function quemFaz(l: LinhaEmConflito): string {
  return l.viatura ?? l.subcontratado ?? '—';
}

/** `2026-09-28T14:32:07.412Z` → `14:32`. ⚠️ Corte e não `Date`: ver o cabeçalho de `ancora.ts`. */
function horaDe(quando: string | undefined): string {
  return typeof quando === 'string' && quando.length >= 16 ? quando.slice(11, 16) : '—';
}

/** O nome, ou o `id` quando não se resolveu — nunca o silêncio. */
function nomeDe(l: LinhaEmConflito, nomes: Readonly<Record<string, string>>): string {
  if (l.quem === null || l.quem === undefined || l.quem === '') return 'outro caminho';
  return nomes[l.quem] ?? l.quem;
}

export interface ConflitoAoGravarProps {
  /** O `recarregar` do mapa — o mesmo que todas as janelas já usam. */
  readonly recarregar?: (() => void) | undefined;
}

/**
 * Regista-se como quem pergunta, e desenha a caixa quando é preciso.
 *
 * ⚠️ **Monta-se uma vez na raiz** e não por painel: o ciclo vive no invólucro do `fetch`, logo
 * quem escreve pode ser qualquer um dos 25 ficheiros — nenhum deles é filho de nenhum outro.
 */
export function ConflitoAoGravar({ recarregar }: ConflitoAoGravarProps) {
  const [corpo, definirCorpo] = useState<CorpoDoConflito | null>(null);
  const [motivo, definirMotivo] = useState('');
  const [diarioAberto, definirDiarioAberto] = useState(false);
  /**
   * Como responder ao ciclo.
   *
   * ⚠️ **Numa referência e não no estado:** resolver a promessa é um efeito colateral, e pô-la
   * no estado fazia cada tecla escrita no motivo redesenhar-se com uma promessa nova por
   * dependência — o ciclo de render que já custou um teste que nunca terminou.
   */
  const responder = useRef<((d: DecisaoDoConflito) => void) | null>(null);

  useEffect(() => {
    const desregistar = registarPerguntadorDoConflito(
      (c) =>
        new Promise<DecisaoDoConflito>((resolver) => {
          responder.current = resolver;
          definirMotivo('');
          definirDiarioAberto(false);
          definirCorpo(c);
        }),
    );
    return () => {
      desregistar();
      // ⚠️⚠️ **DESMONTAR A MEIO DE UMA PERGUNTA RESPONDE «NÃO GRAVAR», e não é cortesia.**
      //    Apanhado pelas provas: sem isto a promessa **nunca resolve**, o ciclo fica preso, e
      //    o módulo — que só admite uma caixa de cada vez — deixa de perguntar **para sempre**.
      //    📌 O sintoma seria o pior desta família: a partir daí todos os conflitos passavam a
      //    não avisar, em silêncio, e a peça inteira desligava-se sem nada o denunciar.
      const r = responder.current;
      responder.current = null;
      if (r !== null) r(null);
    };
  }, []);

  const decidir = (d: DecisaoDoConflito): void => {
    const r = responder.current;
    responder.current = null;
    definirCorpo(null);
    definirDiarioAberto(false);
    // ⚠️ Resolve-se DEPOIS de fechar: a repetição da escrita pode recarregar o mapa, e uma
    //    caixa ainda montada por cima de um mapa a recarregar lê-se como «não fez nada».
    if (r !== null) r(d);
  };

  // ⚠️ `Escape` = «não gravar». É a mesma saída do botão, e é a única que não grava.
  // 🔴 2026-10-22 · pela PILHA das janelas (`useEscape`): este diálogo abre por cima de tudo, logo
  //    entra no topo e só ele fecha — com um ouvinte próprio, um `Escape` fechava o diálogo E a janela
  //    de baixo. 📌 Repete o corpo de `decidir(null)` em vez de o chamar (a lição de 2026-08-12).
  useEscape(() => {
    const r = responder.current;
    responder.current = null;
    definirCorpo(null);
    definirDiarioAberto(false);
    if (r !== null) r(null);
  }, corpo !== null);

  if (corpo === null) return null;

  const contexto = corpo.contexto ?? {};
  const linhas = contexto.linhas ?? [];
  const nomes = contexto.nomes ?? {};
  const total = contexto.total ?? linhas.length;
  const escondidas = total - linhas.length;
  const podeGravar = motivo.trim() !== '';

  return (
    <>
      <div className="conflito-fundo" role="presentation" />
      <section className="conflito-caixa" role="alertdialog" aria-label="O plano mudou">
        <header className="conflito-cab">
          <h2>Isto mudou depois de abrir o ecrã</h2>
        </header>

        {/* ⚠️ A frase do SERVIDOR e não uma minha: é ele que sabe o que mudou, e uma segunda
            redacção aqui divergia da que fica na auditoria. */}
        <p className="conflito-frase">
          {corpo.detalhe ?? 'Outra pessoa mudou estas linhas depois de este ecrã ter carregado.'}
        </p>

        {linhas.length === 0 ? null : (
          <table className="conflito-tabela">
            <thead>
              <tr>
                <th scope="col">Onde</th>
                <th scope="col">Dia</th>
                <th scope="col">Viagem</th>
                <th scope="col">Quando</th>
                <th scope="col">Quem</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l, i) => (
                <tr key={`${quemFaz(l)}-${l.data ?? String(i)}`}>
                  <td>{quemFaz(l)}</td>
                  <td>{l.data ?? '—'}</td>
                  {/* ⚠️ Sem referência é um dia SEM serviço — «—» e não uma cadeia vazia, que
                      se lê como coluna partida. */}
                  <td>{l.referencia ?? '—'}</td>
                  <td>{horaDe(l.quando)}</td>
                  <td>{nomeDe(l, nomes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* ⚠️⚠️ O total que NÃO cabe na amostra diz-se. O `trigger` tem tecto de 10 linhas; sem
            esta frase, «12 linhas» na frase de cima com 10 na tabela lê-se como defeito. */}
        {escondidas > 0 ? (
          <p className="conflito-resto contas-sub">
            …e mais {escondidas} linha(s) — a lista mostra as primeiras {linhas.length}.
          </p>
        ) : null}

        <label className="largar-motivo">
          <span>
            Motivo <strong>obrigatório</strong> {MOTIVO_SUFIXO}
          </span>
          <textarea
            value={motivo}
            onChange={(e) => definirMotivo(e.target.value)}
            rows={2}
            autoFocus
            placeholder={MOTIVO_EXEMPLO}
          />
        </label>

        <div className="conflito-accoes faixa-quebra">
          <button
            type="button"
            className="btn btn--principal"
            disabled={!podeGravar}
            onClick={() => decidir({ motivo: motivo.trim() })}
            title="gravar por cima — fica na auditoria com o motivo e com o que estava lá"
          >
            Gravar mesmo assim
          </button>
          <button type="button" className="btn" onClick={() => definirDiarioAberto(true)}>
            Ver o que mudou
          </button>
          <button type="button" className="btn" onClick={() => decidir(null)}>
            Não gravar
          </button>
          {podeGravar ? null : (
            <span className="contas-sub">escreva o motivo antes de gravar</span>
          )}
        </div>

        {/* ⚠️ `outros` e não `tudo`: quem carrega aqui acabou de ler que a mudança não foi sua.
            É o mesmo filtro, e o mesmo componente, que o `PlanoMudou` já abre. */}
        {diarioAberto ? (
          <Diario
            quemInicial="outros"
            fechar={() => definirDiarioAberto(false)}
            {...(recarregar === undefined ? {} : { recarregarMapa: recarregar })}
          />
        ) : null}
      </section>
    </>
  );
}
